/**
 * Messaging Handlers
 *
 * Pure command handlers for the Messaging subdomain. Mirrors the Invoicing
 * subdomain 1:1: provisioned integrations are keyed in `data.integrations`, the
 * add modal is a single boolean + selected connector, and submit/delete
 * lifecycles are tracked through `loading.submitting` and the integrations
 * record respectively.
 */

import { MessagingState } from './state';
import { MessagingCommand } from './commands';
import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';

type ExtractCommand<T extends string> = Extract<MessagingCommand, { type: T }>;

// List handlers
const handleListSet = (
  state: MessagingState,
  cmd: ExtractCommand<'integrations/messaging/list/set'>
): MessagingState => ({
  ...state,
  data: { ...state.data, integrations: cmd.payload.integrations },
  loading: { ...state.loading, listLoading: false }
});

// Add modal handlers
const handleAddModalOpen = ( state: MessagingState ): MessagingState => ({
  ...state,
  ui: { ...state.ui, addModalOpen: true, selectedConnectorId: null }
});

const handleAddModalClose = ( state: MessagingState ): MessagingState => ({
  ...state,
  ui: { ...state.ui, addModalOpen: false, selectedConnectorId: null },
  loading: { ...state.loading, submitting: false },
  pendingSubmission: null
});

const handleAddModalSelectConnector = (
  state: MessagingState,
  cmd: ExtractCommand<'integrations/messaging/addModal/selectConnector'>
): MessagingState => ({
  ...state,
  ui: { ...state.ui, selectedConnectorId: cmd.payload.connectorId }
});

// Submit handlers
const handleSubmitStart = (
  state: MessagingState,
  cmd: ExtractCommand<'integrations/messaging/submit/start'>
): MessagingState => ({
  ...state,
  loading: { ...state.loading, submitting: true },
  pendingSubmission: {
    connectorId: cmd.payload.connectorId,
    credentials: cmd.payload.credentials,
    label: cmd.payload.label
  }
});

const handleSubmitSuccess = (
  state: MessagingState,
  cmd: ExtractCommand<'integrations/messaging/submit/success'>
): MessagingState => ({
  ...state,
  data: {
    ...state.data,
    integrations: {
      ...state.data.integrations,
      [cmd.payload.integration.id]: cmd.payload.integration
    }
  },
  ui: { ...state.ui, addModalOpen: false, selectedConnectorId: null },
  loading: { ...state.loading, submitting: false },
  pendingSubmission: null
});

const handleSubmitError = ( state: MessagingState ): MessagingState => ({
  ...state,
  loading: { ...state.loading, submitting: false },
  pendingSubmission: null
});

// Delete handlers
const handleDeleteStart = (
  state: MessagingState,
  cmd: ExtractCommand<'integrations/messaging/delete/start'>
): MessagingState => ({
  ...state,
  loading: { ...state.loading, submitting: true },
  pendingDeletion: { integrationId: cmd.payload.integrationId }
});

const handleDeleteSuccess = (
  state: MessagingState,
  cmd: ExtractCommand<'integrations/messaging/delete/success'>
): MessagingState => {
  const { [cmd.payload.integrationId]: _, ...rest } = state.data.integrations;
  return {
    ...state,
    data: { ...state.data, integrations: rest },
    loading: { ...state.loading, submitting: false },
    pendingDeletion: null
  };
};

const handleDeleteError = ( state: MessagingState ): MessagingState => ({
  ...state,
  loading: { ...state.loading, submitting: false },
  pendingDeletion: null
});

export const handlers: CommandHandlerMap<MessagingState, MessagingCommand> = {
  'integrations/messaging/list/set': handleListSet,
  'integrations/messaging/addModal/open': handleAddModalOpen,
  'integrations/messaging/addModal/close': handleAddModalClose,
  'integrations/messaging/addModal/selectConnector': handleAddModalSelectConnector,
  'integrations/messaging/submit/start': handleSubmitStart,
  'integrations/messaging/submit/success': handleSubmitSuccess,
  'integrations/messaging/submit/error': handleSubmitError,
  'integrations/messaging/delete/start': handleDeleteStart,
  'integrations/messaging/delete/success': handleDeleteSuccess,
  'integrations/messaging/delete/error': handleDeleteError
};
